import { Typography } from '@mui/material';
import { clsx } from 'clsx';
import React, { memo, useCallback, useMemo, useState } from 'react';

import chart from '~/assets/icons/chart.svg';
import { ChartPopover } from '~/pages/Company/components/Inference/components/ChartPopover';
import { CoInvestmentScore } from '~/pages/Company/components/Inference/components/CoInvestmentScore';
import { GetCompanyInferenceResponse } from '~/services/rtkQuery/company/types';

import styles from './styles.module.scss';

interface Props {
  rating: number;
  data: GetCompanyInferenceResponse['result']['data']['investors_score'];
  barChartData: GetCompanyInferenceResponse['result']['data']['supporting_graphs']['companies_scores'];
}

const Component: React.FC<Props> = ({ rating, data, barChartData }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleChartButtonClick: React.MouseEventHandler<HTMLButtonElement> = useCallback(event => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleChartPopoverClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const labels = useMemo(() => {
    return data.quartiles.reduce<number[]>((acc, current, index, array) => {
      const newAcc = [...acc, current.bottom_th];

      if (index === array.length - 1) {
        newAcc.push(current.top_th);
      }

      return newAcc;
    }, []);
  }, [data.quartiles]);

  const renderBar = useCallback(
    (
      quartile: GetCompanyInferenceResponse['result']['data']['investors_score']['quartiles'][number],
      index: number,
    ) => {
      const names: Record<
        GetCompanyInferenceResponse['result']['data']['investors_score']['quartiles'][number]['name'],
        string
      > = {
        loss: 'Loss Making',
        poor: 'Poor',
        below_av: 'Bellow Average',
        above_av: 'Above Average',
        exellent: 'Excellent',
      };

      return (
        <div
          className={clsx(styles.chart__bar, {
            [styles['chart__bar--last']]: index === data.quartiles.length - 1,
            [styles['chart__bar--loss']]: quartile.name === 'loss',
            [styles['chart__bar--poor']]: quartile.name === 'poor',
            [styles['chart__bar--below']]: quartile.name === 'below_av',
            [styles['chart__bar--above']]: quartile.name === 'above_av',
            [styles['chart__bar--excellent']]: quartile.name === 'exellent',
          })}
          key={quartile.name}
        >
          {quartile.active && (
            <Typography color="var(--text-3-color)" fontWeight="600" mb="26px" variant="h6">
              {data.rating.toFixed(1)}
            </Typography>
          )}
          <Typography color="var(--text-3-color-50)" variant="subtitle2">
            {names[quartile.name]}
          </Typography>
        </div>
      );
    },
    [data.quartiles.length, data.rating],
  );

  const renderLabel = useCallback((label: number, index: number) => {
    return (
      <Typography
        align="center"
        color="var(--gray-2-color)"
        fontWeight="300"
        key={label}
        left={`calc(${index * 20}% - 25px)`}
        position="absolute"
        variant="subtitle1"
        width="50px"
      >
        {label}
      </Typography>
    );
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.container__heading}>
        <Typography color="var(--text-3-color)" fontWeight="500" mr="10px" variant="subtitle1">
          Co-Investor Score
        </Typography>
        <div className={styles['container__confidence-level']}>
          <Typography color="var(--text-3-color)" mr="8px" variant="subtitle1">
            Investments / Investor {data.investments_per_investor.toFixed(1)} Confidence level:
            <Typography
              color={data.confidence === 'low' ? 'var(--error-color)' : 'var(--success-color)'}
              component="span"
              fontWeight="700"
              variant="subtitle1"
            >
              {data.confidence === 'high'
                ? 'HIGH'
                : data.confidence === 'medium'
                  ? 'MEDIUM'
                  : 'LOW'}
              *
            </Typography>
          </Typography>
          <button type="button" onClick={handleChartButtonClick}>
            <img alt="chart" src={chart} />
          </button>
          <ChartPopover anchorEl={anchorEl} onClose={handleChartPopoverClose}>
            <CoInvestmentScore
              className={styles['container__bar-chart']}
              data={barChartData}
              rating={rating}
            />
          </ChartPopover>
        </div>
      </div>
      <div className={styles.chart}>
        <div className={styles.chart__bars}>{data.quartiles.map(renderBar)}</div>
        <div className={styles['chart__x-axis-line']} />
        <div className={styles['chart__x-axis-labels']}>{labels.map(renderLabel)}</div>
      </div>
    </div>
  );
};

export const CoInvestorScore = memo(Component);
